import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useMemo } from "react";
import { useIntl } from "react-intl";

// Components
import {
    Modal, ModalBody, ModalFooter, Button, Select, Tabs, TabContainer,
} from "@gpstrackit/react-controls";
import { NotificationManager } from 'Components/Notifications';

// Helpers
import { copyToClipboard, getVideoReviewStatusOptions } from 'Core/data/Helpers';

// Actions
import { edgeTensorVideoReviewGetEvent, edgeTensorVideoReviewSave, reset } from './redux/slice';


const EdgeTensorVideoReview = (props) => {
    const { messages } = useIntl();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const {
        openModal, data, result, reviewEvent, error, loading
    } = useSelector(state => state.edgeTensorVideoReviewRedux);


    const [notes, setNotes] = useState('');
    const [status, setStatus] = useState({ id: 'Unreviewed', label: messages['edge_tensor_video_review_unreviewed'] });
    const [tab, setTab] = useState(data?.tab || 0);
    const closeModal = () => {
        dispatch(reset());
    };

    const handleSave = () => {
        try {

            dispatch(edgeTensorVideoReviewSave({
                rowId: data.rowId,
                deviceId: data.deviceId,
                companyId: data.accountId,
                fieldName: 'videoReview',
                jsonValue: JSON.stringify({ description: notes, status: status.id, type: 'admin', oldStatus: "Unreviewed" }),
                visibleToDriver: false
            }));
        } catch (error) {
            NotificationManager.error(
                messages['edge_tensor_video_review_error_messages'],
                messages['edge_tensor_video_review_title'],
                3000
            );
        }
    };

    useEffect(() => {
        if (openModal && data) {
            //search video review event
            dispatch(edgeTensorVideoReviewGetEvent({ rowId: data.rowId }));
        }
    }, [openModal]);

    useEffect(() => {
        if (result) {
            const message = messages["edge_tensor_video_review_success_messages"];
            NotificationManager.success(message, messages['edge_tensor_video_review_title'], 10000);
            props.reloadData && props.reloadData(null, null, true);
            closeModal();
        }

        if (error) {
            NotificationManager.error(
                messages['edge_tensor_video_review_error_messages'],
                messages['edge_tensor_video_review_title'],
                3000
            );
            dispatch(reset({ error: null }));
        }
    }, [result, error]);

    return (
        <>
            <Modal
                open={openModal}
                title={`${data.onlyVideo ? messages['edge_tensor_video_review_title'] : messages['edge_tensor_video_review_video']} ${data?.rowId ? `- ${data?.rowId} - ${data.eventName}` : ''}`}
                handleClose={closeModal}
                position="right"
                loading={loading}
                size="small"
            >
                <ModalBody>
                    <Tabs tab={+tab} onChange={(numberTab) => { setTab(numberTab) }}>
                        <TabContainer index={0} key={`menu-${0}`} label={!data.onlyVideo ? messages['edge_tensor_video_review']: messages['edge_tensor_video_review_event_video']}>
                            <div style={{ padding: '0 10px' }}>
                                <div style={{ width: '100%', display: 'grid', marginBottom: 20 }}>
                                    <div>
                                        <label>{messages['edge_tensor_video_review_video']}</label>
                                        <video
                                            src={reviewEvent?.deviceReading?.deviceData?.url || ''}   // o URL absoluta
                                            controls                    // muestra controles nativos
                                            width="100%"                // puedes controlar con CSS para responsivo
                                            preload="metadata"          // "auto" | "metadata" | "none"
                                        >
                                            <code>video</code>.
                                        </video>
                                    </div>
                                    {
                                        !data.onlyVideo &&
                                        <div style={{ marginTop: 20 }}>
                                            <label>{messages['edge_tensor_video_review_notes']}</label>
                                            <textarea
                                                style={{ minHeight: 50 }}
                                                value={reviewEvent?.deviceReading?.videoReview?.description ? reviewEvent?.deviceReading?.videoReview?.description : notes || ""}
                                                onChange={(e) => {
                                                    let value = e.target.value;
                                                    setNotes(value);
                                                }}
                                                placeholder={messages['edge_tensor_video_review_notes']}
                                                className="form-control"
                                                maxLength={1000}
                                                minLength={3}
                                                disabled={!!reviewEvent?.deviceReading?.videoReview?.status}
                                            />
                                        </div>
                                    }
                                    {
                                        !data.onlyVideo &&
                                        <div style={{ marginTop: 20 }}>
                                            <Select
                                                id="video-review-status"
                                                label={messages['edge_tensor_video_review_status']}
                                                placeholder={messages['edge_tensor_video_review_status']}
                                                options={getVideoReviewStatusOptions(messages)}
                                                value={reviewEvent?.deviceReading?.videoReview?.status ? getVideoReviewStatusOptions(messages).find(option => option.id === reviewEvent?.deviceReading?.videoReview?.status) : status}
                                                textField="label"
                                                valueField="id"
                                                onChange={(newStatus) => setStatus(newStatus)}
                                                disabled={!!reviewEvent?.deviceReading?.videoReview?.status}
                                                required
                                                requiredMessage={messages['required_field']}
                                            />
                                        </div>
                                    }
                                </div>
                            </div>
                        </TabContainer>
                        {
                            !data.onlyVideo &&
                            <TabContainer index={1} key={`menu-${1}`} label={messages['edge_tensor_video_review_video_results']}>
                                <div style={{ padding: '0 10px', marginBottom: 20 }}>
                                    {reviewEvent?.urlsOverview && reviewEvent?.urlsOverview.length > 0 ? (
                                        reviewEvent.urlsOverview.map((urlObj, index) => (
                                            <div>
                                                <label>{`${messages['edge_tensor_video_review_video_result']} - ${index + 1}`}</label>
                                                <video
                                                    src={urlObj || ''}   // o URL absoluta
                                                    controls                    // muestra controles nativos
                                                    width="100%"                // puedes controlar con CSS para responsivo
                                                    preload="metadata"          // "auto" | "metadata" | "none"
                                                >
                                                    <code>video</code>.
                                                </video>
                                            </div>
                                        ))
                                    ) : (
                                        <></>
                                    )}
                                </div>
                            </TabContainer>
                        }
                    </Tabs>

                </ModalBody>
                <ModalFooter>
                    <Button className="btn btn-cancel" onClick={closeModal}>
                        {messages['common_cancel']}
                    </Button>
                    {
                        !data.onlyVideo &&
                        <Button
                            className="btn-dark-blue"
                            disabled={reviewEvent?.deviceReading?.videoReview?.status}
                            onClick={handleSave}
                        >
                            {messages['common_save']}
                        </Button>
                    }
                </ModalFooter>
            </Modal>
        </>
    );
};

export default EdgeTensorVideoReview;